import { useVirtualizer } from '@tanstack/react-virtual';
import {
  Children,
  isValidElement,
  type ReactNode,
  useEffect,
  useState,
} from 'react';

const GAP = 12;
const ESTIMATED_ITEM_HEIGHT = 320;
const OVERSCAN_ITEMS = 50;

const getLaneCount = (maxLanes: number) => {
  if (window.matchMedia('(min-width: 1024px)').matches) return maxLanes;
  if (window.matchMedia('(min-width: 640px)').matches)
    return Math.min(3, maxLanes);
  return Math.min(2, maxLanes);
};

interface MasonryGridProps {
  children: ReactNode;
  scrollElement: HTMLDivElement | null;
  estimatedItemHeight?: number;
  overscanItems?: number;
  maxLanes?: number;
}

export const MasonryGrid = ({
  children,
  scrollElement,
  estimatedItemHeight = ESTIMATED_ITEM_HEIGHT,
  overscanItems = OVERSCAN_ITEMS,
  maxLanes = 4,
}: MasonryGridProps) => {
  const items = Children.toArray(children);
  const [laneCount, setLaneCount] = useState(() => getLaneCount(maxLanes));

  useEffect(() => {
    const smallMediaQuery = window.matchMedia('(min-width: 640px)');
    const largeMediaQuery = window.matchMedia('(min-width: 1024px)');
    const updateLaneCount = () => setLaneCount(getLaneCount(maxLanes));

    smallMediaQuery.addEventListener('change', updateLaneCount);
    largeMediaQuery.addEventListener('change', updateLaneCount);

    return () => {
      smallMediaQuery.removeEventListener('change', updateLaneCount);
      largeMediaQuery.removeEventListener('change', updateLaneCount);
    };
  }, [maxLanes]);

  const virtualizer = useVirtualizer({
    count: items.length,
    estimateSize: () => estimatedItemHeight,
    getItemKey: (index) => {
      const item = items[index];
      return isValidElement(item) && item.key !== null ? item.key : index;
    },
    getScrollElement: () => scrollElement,
    gap: GAP,
    laneAssignmentMode: 'measured',
    lanes: laneCount,
    overscan: overscanItems,
  });

  return (
    <div
      className="relative w-full"
      style={{ height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((virtualItem) => (
        <div
          key={virtualItem.key}
          data-index={virtualItem.index}
          ref={virtualizer.measureElement}
          className="absolute top-0"
          style={{
            left: `calc(${virtualItem.lane} * ((100% + ${GAP}px) / ${laneCount}))`,
            width: `calc((100% - ${(laneCount - 1) * GAP}px) / ${laneCount})`,
            transform: `translateY(${virtualItem.start}px)`,
          }}
        >
          {items[virtualItem.index]}
        </div>
      ))}
    </div>
  );
};
